
import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

const Navigation = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const navigate = useNavigate();
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setActiveDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const scrollToSection = (sectionId: string) => {
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
    setIsMobileMenuOpen(false);
    setActiveDropdown(null);
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    setIsMobileMenuOpen(false);
    setActiveDropdown(null);
  };

  const toggleDropdown = (dropdown: string) => {
    setActiveDropdown(activeDropdown === dropdown ? null : dropdown);
  };

  const spiritualServices = [
    { name: 'Hope Ritual', path: '/hope-ritual', description: '30-Day Self-Love Journey' },
    { name: 'Shadow Work', path: '/shadow-work', description: 'Ancestral Healing & Self-Discovery' },
    { name: 'Trap Tarot', path: '/trap-tarot', description: 'Intuitive Card Readings' },
  ];

  const contentPages = [
    { name: 'Vlogs', path: '/vlogs', description: 'Spiritual Guidance Videos' },
    { name: 'Reminders', path: '/reminders', description: 'Daily Affirmations' },
  ];

  return (
    <nav className={`fixed w-full z-50 transition-all duration-300 ${
      isScrolled ? 'bg-white/95 backdrop-blur-md shadow-lg' : 'bg-transparent'
    }`} ref={dropdownRef}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          <div 
            onClick={() => handleNavigation('/')}
            className="text-2xl font-bold cursor-pointer" 
            style={{ fontFamily: '"Pacifico", serif' }}
          >
            <span className={`transition-colors duration-300 ${
              isScrolled ? 'text-amber-700' : 'text-white'
            }`}>
              Sacred Frequency
            </span>
          </div>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center space-x-6">
            <button 
              onClick={() => scrollToSection('hero')}
              className={`font-medium transition-colors duration-300 cursor-pointer whitespace-nowrap ${
                isScrolled ? 'text-stone-700 hover:text-amber-600' : 'text-white hover:text-amber-200'
              }`}
            >
              Home
            </button>

            <button 
              onClick={() => scrollToSection('about')}
              className={`font-medium transition-colors duration-300 cursor-pointer whitespace-nowrap ${
                isScrolled ? 'text-stone-700 hover:text-amber-600' : 'text-white hover:text-amber-200'
              }`}
            >
              About
            </button>

            {/* Spiritual Services Dropdown */}
            <div className="relative">
              <button 
                onClick={() => toggleDropdown('services')}
                className={`font-medium transition-colors duration-300 cursor-pointer whitespace-nowrap flex items-center gap-1 ${
                  isScrolled ? 'text-stone-700 hover:text-amber-600' : 'text-white hover:text-amber-200'
                }`}
              >
                Services
                <i className={`ri-arrow-down-s-line transition-transform duration-200 ${
                  activeDropdown === 'services' ? 'rotate-180' : ''
                }`}></i>
              </button>
              
              {activeDropdown === 'services' && (
                <div className="absolute top-full left-0 mt-2 w-64 bg-white rounded-lg shadow-xl border border-gray-100 py-2 z-50">
                  {spiritualServices.map((service) => (
                    <button
                      key={service.path}
                      onClick={() => handleNavigation(service.path)}
                      className="w-full text-left px-4 py-3 hover:bg-amber-50 transition-colors group"
                    >
                      <div className="font-medium text-stone-700 group-hover:text-amber-600">
                        {service.name}
                      </div>
                      <div className="text-sm text-stone-500 mt-1">
                        {service.description}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Content Dropdown */}
            <div className="relative">
              <button 
                onClick={() => toggleDropdown('content')}
                className={`font-medium transition-colors duration-300 cursor-pointer whitespace-nowrap flex items-center gap-1 ${
                  isScrolled ? 'text-stone-700 hover:text-amber-600' : 'text-white hover:text-amber-200'
                }`}
              >
                Content
                <i className={`ri-arrow-down-s-line transition-transform duration-200 ${
                  activeDropdown === 'content' ? 'rotate-180' : ''
                }`}></i>
              </button>
              
              {activeDropdown === 'content' && (
                <div className="absolute top-full left-0 mt-2 w-64 bg-white rounded-lg shadow-xl border border-gray-100 py-2 z-50">
                  {contentPages.map((page) => (
                    <button
                      key={page.path}
                      onClick={() => handleNavigation(page.path)}
                      className="w-full text-left px-4 py-3 hover:bg-amber-50 transition-colors group"
                    >
                      <div className="font-medium text-stone-700 group-hover:text-amber-600">
                        {page.name}
                      </div>
                      <div className="text-sm text-stone-500 mt-1">
                        {page.description}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <button 
              onClick={() => handleNavigation('/products')}
              className={`font-medium transition-colors duration-300 cursor-pointer whitespace-nowrap ${
                isScrolled ? 'text-stone-700 hover:text-amber-600' : 'text-white hover:text-amber-200'
              }`}
            >
              Shop
            </button>

            <button 
              onClick={() => scrollToSection('testimonials')}
              className={`font-medium transition-colors duration-300 cursor-pointer whitespace-nowrap ${
                isScrolled ? 'text-stone-700 hover:text-amber-600' : 'text-white hover:text-amber-200'
              }`}
            >
              Testimonials
            </button>

            <button 
              onClick={() => scrollToSection('contact')}
              className={`font-medium transition-colors duration-300 cursor-pointer whitespace-nowrap ${
                isScrolled ? 'text-stone-700 hover:text-amber-600' : 'text-white hover:text-amber-200'
              }`}
            >
              Contact
            </button>

            <button 
              onClick={() => handleNavigation('/products')}
              className="bg-gradient-to-r from-amber-600 to-red-600 text-white px-6 py-2 rounded-full font-semibold hover:from-amber-700 hover:to-red-700 transform hover:scale-105 transition-all duration-300 shadow-lg cursor-pointer whitespace-nowrap"
            >
              Shop Now
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`md:hidden transition-colors duration-300 cursor-pointer ${
              isScrolled ? 'text-stone-700' : 'text-white'
            }`}
          >
            <div className="w-6 h-6 flex items-center justify-center">
              <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
            </div>
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-stone-200 py-4">
            <div className="flex flex-col space-y-4">
              <button 
                onClick={() => scrollToSection('hero')}
                className="text-stone-700 hover:text-amber-600 font-medium transition-colors duration-300 cursor-pointer whitespace-nowrap text-left px-4"
              >
                Home
              </button>
              
              <button 
                onClick={() => scrollToSection('about')}
                className="text-stone-700 hover:text-amber-600 font-medium transition-colors duration-300 cursor-pointer whitespace-nowrap text-left px-4"
              >
                About
              </button>

              {/* Mobile Services Section */}
              <div className="px-4">
                <div className="font-medium text-stone-700 mb-2">Services</div>
                <div className="pl-4 space-y-2">
                  {spiritualServices.map((service) => (
                    <button
                      key={service.path}
                      onClick={() => handleNavigation(service.path)}
                      className="block text-stone-600 hover:text-amber-600 transition-colors text-left"
                    >
                      {service.name}
                    </button>
                  ))}
                </div>
              </div>

              {/* Mobile Content Section */}
              <div className="px-4">
                <div className="font-medium text-stone-700 mb-2">Content</div>
                <div className="pl-4 space-y-2">
                  {contentPages.map((page) => (
                    <button
                      key={page.path}
                      onClick={() => handleNavigation(page.path)}
                      className="block text-stone-600 hover:text-amber-600 transition-colors text-left"
                    >
                      {page.name}
                    </button>
                  ))}
                </div>
              </div>

              <button 
                onClick={() => handleNavigation('/products')}
                className="text-stone-700 hover:text-amber-600 font-medium transition-colors duration-300 cursor-pointer whitespace-nowrap text-left px-4"
              >
                Shop
              </button>

              <button 
                onClick={() => scrollToSection('testimonials')}
                className="text-stone-700 hover:text-amber-600 font-medium transition-colors duration-300 cursor-pointer whitespace-nowrap text-left px-4"
              >
                Testimonials
              </button>

              <button 
                onClick={() => scrollToSection('contact')}
                className="text-stone-700 hover:text-amber-600 font-medium transition-colors duration-300 cursor-pointer whitespace-nowrap text-left px-4"
              >
                Contact
              </button>

              <button 
                onClick={() => handleNavigation('/products')}
                className="bg-gradient-to-r from-amber-600 to-red-600 text-white px-6 py-2 rounded-full font-semibold hover:from-amber-700 hover:to-red-700 transition-all duration-300 shadow-lg cursor-pointer whitespace-nowrap mx-4"
              >
                Shop Now
              </button>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;
